import { Injectable, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  IBaseGridState,
  IGridWithState,
  IGridStateManager,
  IGridStateManagerConfig,
} from './grid-state-manager.interface';

/**
 * Universal service for grid state management
 */
@Injectable()
export class GridStateManagerService<TState extends IBaseGridState> implements IGridStateManager<TState>, OnDestroy {
  private config: IGridStateManagerConfig<TState>;
  private subscription: Subscription | null = null;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
  ) {}

  /**
   * Initializes state management
   */
  initialize(config: IGridStateManagerConfig<TState>): void {
    this.config = config;
    this.loadState();
  }

  /**
   * Loads state from URL or sessionStorage
   */
  loadState(): void {
    if (!this.config) {
      console.warn('GridStateManager not initialized');
      return;
    }

    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }

    const paramName = this.config.paramName || 'state';

    this.subscription = this.activatedRoute.queryParams
      .pipe(
        map((params) => {
          if (params[paramName]) {
            const parsed = this.parseStateFromUrl(params[paramName]);
            if (parsed !== null) {
              return parsed;
            }
          }
          return this.loadStateFromStorage();
        }),
      )
      .subscribe((state) => {
        if (state) {
          const currentStateStr = JSON.stringify(this.getCurrentState());
          const newStateStr = JSON.stringify(state);

          if (currentStateStr !== newStateStr) {
            this.applyState(state);
          }
        }
      });
  }

  /**
   * Saves the current state
   */
  saveState(): void {
    if (!this.config) {
      console.warn('GridStateManager not initialized');
      return;
    }

    const currentState = this.getCurrentState();
    if (!currentState) {
      return;
    }

    const stateString = JSON.stringify(currentState);

    const storageKey = this.getStorageKey();
    if (storageKey) {
      sessionStorage.setItem(storageKey, stateString);
    }

    const stateWithId = {
      ...currentState,
      _gridId: storageKey,
    };
    const urlStateString = JSON.stringify(stateWithId);

    const paramName = this.config.paramName || 'state';

    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { [paramName]: this.safeBtoa(urlStateString) },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /**
   * Applies state to component
   */
  applyState(state: TState): void {
    if (!this.config) {
      console.warn('GridStateManager not initialized');
      return;
    }

    if (this.config.applyState) {
      this.config.applyState(state);
    }
  }

  /**
   * Resets state to default values
   */
  resetState(): void {
    if (!this.config) {
      console.warn('GridStateManager not initialized');
      return;
    }

    const storageKey = this.getStorageKey();
    if (storageKey) {
      sessionStorage.removeItem(storageKey);
    }

    const paramName = this.config.paramName || 'state';
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { [paramName]: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /**
   * Gets the current state
   */
  getCurrentState(): TState | null {
    if (!this.config) {
      console.warn('GridStateManager not initialized');
      return null;
    }

    if (this.config.getCurrentState) {
      return this.config.getCurrentState();
    }

    return null;
  }

  /**
   * Destroys the service and unsubscribes from subscriptions
   */
  destroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
  }

  /**
   * Implementation of OnDestroy interface
   */
  ngOnDestroy(): void {
    this.destroy();
  }

  /**
   * Loads state from sessionStorage
   */
  private loadStateFromStorage(): TState | null {
    const storageKey = this.getStorageKey();
    if (!storageKey) {
      return null;
    }

    return this.parseStateFromStorage(storageKey);
  }

  /**
   * Parses state from URL and validates the grid identifier to prevent race conditions
   */
  private parseStateFromUrl(encodedState: string): TState | null {
    try {
      const decodedStr = this.safeAtob(encodedState);
      if (!decodedStr) {
        return null;
      }
      const decoded = JSON.parse(decodedStr);
      const storageKey = this.getStorageKey();

      if (decoded && decoded._gridId && decoded._gridId !== storageKey) {
        console.warn(`GridStateManager: Ignored stale state for grid '${decoded._gridId}' (current grid is '${storageKey}')`);
        return null;
      }

      if (decoded && typeof decoded === 'object') {
        delete decoded._gridId;
      }
      return this.normalizeState(decoded);
    } catch (e) {
      console.error('Error parsing grid state from query params:', e);
      return null;
    }
  }

  /**
   * Parses state from sessionStorage
   */
  private parseStateFromStorage(storageKey: string): TState | null {
    try {
      const savedState = sessionStorage.getItem(storageKey);
      if (!savedState) {
        return null;
      }

      const parsed = JSON.parse(savedState);
      return this.normalizeState(parsed);
    } catch (e) {
      sessionStorage.removeItem(storageKey);
      console.error('Error parsing grid state from sessionStorage:', e);
      return null;
    }
  }

  /**
   * Normalizes state
   */
  private normalizeState(state: any): TState | null {
    if (!this.config.normalizeState) {
      return state;
    }

    return this.config.normalizeState(state);
  }

  /**
   * Gets the key for storing state
   */
  private getStorageKey(): string | null {
    if (!this.config) {
      return null;
    }

    return this.config.storageKey;
  }

  /**
   * Safe Base64 encoding that supports UTF-8 characters (e.g. accented letters, special symbols)
   */
  private safeBtoa(str: string): string {
    try {
      return btoa(encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (_, p1) => {
        return String.fromCharCode(parseInt(p1, 16));
      }));
    } catch (e) {
      console.error('GridStateManager: Error encoding state to base64:', e);
      return '';
    }
  }

  /**
   * Safe Base64 decoding that supports UTF-8 characters
   */
  private safeAtob(str: string): string {
    try {
      return decodeURIComponent(atob(str).split('').map((c) => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
    } catch (e) {
      console.error('GridStateManager: Error decoding state from base64:', e);
      return '';
    }
  }
}

/**
 * Factory for creating state normalizers
 */
export class GridStateNormalizers {
  /**
   * Creates a normalizer for state with Kendo state and filter
   */
  static createGridWithFilterNormalizer<TFilter extends { Start?: Date; End?: Date }>(
    defaultFilter: TFilter,
  ): (state: any) => IGridWithState<TFilter> | null {
    return (state: any) => {
      if (!state?.filter) {
        return state;
      }

      return {
        ...state,
        filter: {
          ...state.filter,
          Start: state.filter.Start ? new Date(state.filter.Start) : defaultFilter.Start,
          End: state.filter.End ? new Date(state.filter.End) : defaultFilter.End,
        } as TFilter,
      };
    };
  }
}
